'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { PitchDetector } from 'pitchy';

export interface PitchResult {
  note: string;
  frequency: number;
  cents: number;
  clarity: number;
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

function freqToNote(freq: number): PitchResult {
  const semitones = 12 * Math.log2(freq / 440);
  const rounded = Math.round(semitones);
  const cents = Math.round((semitones - rounded) * 100);
  const octave = Math.floor((rounded + 9) / 12) + 4;
  const noteIdx = ((rounded % 12) + 12) % 12;
  return {
    note: NOTE_NAMES[noteIdx] + octave,
    frequency: Math.round(freq),
    cents,
    clarity: 0,
  };
}

export function usePitchDetection() {
  const [isRunning, setIsRunning] = useState(false);
  const [pitch, setPitch] = useState<PitchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number>(0);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 4096;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const detector = PitchDetector.forFloat32Array(analyser.fftSize);
      const buffer = new Float32Array(analyser.fftSize);

      const detect = () => {
        analyser.getFloatTimeDomainData(buffer);
        const [freq, clarity] = detector.findPitch(buffer, audioCtx.sampleRate);
        if (clarity > 0.9 && freq > 60 && freq < 2000) {
          const result = freqToNote(freq);
          setPitch({ ...result, clarity });
        } else {
          setPitch(null);
        }
        frameRef.current = requestAnimationFrame(detect);
      };

      setIsRunning(true);
      setError(null);
      detect();
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  const stop = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    audioCtxRef.current?.close();
    setIsRunning(false);
    setPitch(null);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { isRunning, pitch, error, start, stop };
}
